// "Clean up the board": the main board's handwriting becomes typed elements. These helpers
// capture the ink, turn the model's answer into sticky elements at the same coordinates,
// apply the change (elements in, ink out, students reloaded) and undo it from a snapshot.
// Main board, admin only: the multi-board paths have their own persistence and are not
// handled here.

import boardManager from '../../pages/LessonPage/boardManager';
import cloudBoardManager from '../../pages/LessonPage/cloudBoardManager';
import drawUtils from '../../pages/LessonPage/drawUtils';
import lessonList from '../../pages/LessonPage/lessonList';
import socketUtils from '../../pages/LessonPage/socketUtils';
import { addLatexToWindowCache } from '../../common/utils';
import { CONNECTOR_TYPES, STROKE_STYLES } from '../../constants';

export const TIDY_LIMITS = { MIN_W: 40, MIN_H: 24, MIN_CONNECTOR: 8, MAX: 60 };
const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const toNumber = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);

// Any pixel with ink on a 1/8 downscale means the board is not blank.
export function isCanvasBlank(canvas) {
    const sample = document.createElement('canvas');
    sample.width = Math.max(1, Math.round(canvas.width / 8));
    sample.height = Math.max(1, Math.round(canvas.height / 8));
    const ctx = sample.getContext('2d');
    ctx.drawImage(canvas, 0, 0, sample.width, sample.height);
    const { data } = ctx.getImageData(0, 0, sample.width, sample.height);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 0) return false;
    }
    return true;
}

// Reads the live canvas once. `dataUrl` keeps the transparency (what revert draws back);
// `blob` is the same ink composited on white, which is what the model gets to look at.
export async function captureBoardSnapshot() {
    const canvas = drawUtils.getCanvas();
    if (!canvas) throw new Error('NO_CANVAS');
    const dataUrl = canvas.toDataURL('image/png');
    if (isCanvasBlank(canvas)) {
        return { dataUrl, blob: null, isEmpty: true };
    }
    const off = document.createElement('canvas');
    off.width = canvas.width;
    off.height = canvas.height;
    const ctx = off.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, off.width, off.height);
    ctx.drawImage(canvas, 0, 0);
    const blob = await new Promise((resolve) => off.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('NO_BLOB');
    return { dataUrl, blob, isEmpty: false };
}

function clampBox(component, W, H) {
    const x = clamp(toNumber(component.x, 0), 0, W - TIDY_LIMITS.MIN_W);
    const y = clamp(toNumber(component.y, 0), 0, H - TIDY_LIMITS.MIN_H);
    const width = clamp(toNumber(component.width, 300), TIDY_LIMITS.MIN_W, W - x);
    const height = clamp(toNumber(component.height, 50), TIDY_LIMITS.MIN_H, H - y);
    return { x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
}

// Deterministic tidying on top of what the model did: connectors within a few degrees of an
// axis (or a 45-degree diagonal) are rotated onto it around their midpoint; element boxes that
// nearly share a left edge or a top are aligned to one value; connector endpoints that nearly
// touch a box edge are put on it. Works in canvas pixels on a copy of the components.
export const SNAP = { AXIS_DEG: 12, DIAGONAL_DEG: 8, EDGE_PX: 20, ALIGN_X_PX: 28, ALIGN_Y_PX: 20 };

function snapAngle(x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    const candidates = [0, 90, 180, -90, -180, 45, 135, -45, -135];
    let best = null;
    for (const target of candidates) {
        const tolerance = target % 90 === 0 ? SNAP.AXIS_DEG : SNAP.DIAGONAL_DEG;
        const diff = Math.abs(angle - target);
        if (diff <= tolerance && (best === null || diff < best.diff)) best = { target, diff };
    }
    if (!best || best.diff === 0) return null;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    // Horizontal / vertical: keep the endpoints' positions along the axis (they may already sit
    // on box edges) and only level the other coordinate. Diagonals rotate around the midpoint.
    if (best.target % 180 === 0) return { x1, y1: my, x2, y2: my };
    if (Math.abs(best.target) === 90) return { x1: mx, y1, x2: mx, y2 };
    const rad = (best.target * Math.PI) / 180;
    const half = length / 2;
    return {
        x1: mx - Math.cos(rad) * half,
        y1: my - Math.sin(rad) * half,
        x2: mx + Math.cos(rad) * half,
        y2: my + Math.sin(rad) * half,
    };
}

// Groups nearly equal values (sorted, greedy) and returns a map value -> group median.
function alignValues(values, tolerance) {
    const sorted = [...new Set(values)].sort((a, b) => a - b);
    const mapping = new Map();
    let group = [];
    const flush = () => {
        if (group.length === 0) return;
        const median = group[Math.floor(group.length / 2)];
        group.forEach((v) => mapping.set(v, median));
        group = [];
    };
    sorted.forEach((v) => {
        if (group.length > 0 && v - group[group.length - 1] > tolerance) flush();
        group.push(v);
    });
    flush();
    return mapping;
}

export function snapTidyComponents(components) {
    const list = (components || []).map((c) => ({ ...c }));
    const boxes = list.filter((c) => c.type !== 'connector' && Number.isFinite(Number(c.x)) && Number.isFinite(Number(c.y)));

    // Shared left edges and tops.
    const xMap = alignValues(boxes.map((c) => Number(c.x)), SNAP.ALIGN_X_PX);
    const yMap = alignValues(boxes.map((c) => Number(c.y)), SNAP.ALIGN_Y_PX);
    // Boxes move as a whole: resizing would letterbox a drawing or reflow a text block.
    boxes.forEach((c) => {
        const nx = xMap.get(Number(c.x));
        const ny = yMap.get(Number(c.y));
        if (nx !== undefined) c.x = nx;
        if (ny !== undefined) c.y = ny;
    });

    // Connectors: endpoints onto nearby box edges, then onto the nearest axis or diagonal.
    const snapEndpoint = (px, py) => {
        let x = px;
        let y = py;
        for (const b of boxes) {
            const left = Number(b.x);
            const top = Number(b.y);
            const right = left + Number(b.width);
            const bottom = top + Number(b.height);
            const insideY = y >= top - SNAP.EDGE_PX && y <= bottom + SNAP.EDGE_PX;
            const insideX = x >= left - SNAP.EDGE_PX && x <= right + SNAP.EDGE_PX;
            if (insideY && Math.abs(x - left) <= SNAP.EDGE_PX) x = left;
            else if (insideY && Math.abs(x - right) <= SNAP.EDGE_PX) x = right;
            if (insideX && Math.abs(y - top) <= SNAP.EDGE_PX) y = top;
            else if (insideX && Math.abs(y - bottom) <= SNAP.EDGE_PX) y = bottom;
        }
        return [x, y];
    };
    list.forEach((c) => {
        if (c.type !== 'connector') return;
        [c.x1, c.y1] = snapEndpoint(Number(c.x1), Number(c.y1));
        [c.x2, c.y2] = snapEndpoint(Number(c.x2), Number(c.y2));
        const snapped = snapAngle(c.x1, c.y1, c.x2, c.y2);
        if (snapped) {
            Object.assign(c, snapped);
            // Levelling can move an end off the edge it sat on; put it back.
            [c.x1, c.y1] = snapEndpoint(c.x1, c.y1);
            [c.x2, c.y2] = snapEndpoint(c.x2, c.y2);
        }
        c.x1 = Math.round(c.x1);
        c.y1 = Math.round(c.y1);
        c.x2 = Math.round(c.x2);
        c.y2 = Math.round(c.y2);
    });
    return list;
}

// The model's coordinates are the canvas coordinates, so they are used as they come (clamped),
// unlike the exercise flow which lays components out in columns.
// textFont: the text tool's current family, style and weight, so typed text matches what the
// teacher would get by typing it (the model only says whether a line is emphasised).
export function buildTidyElements(rawComponents, { fallbackColor = '#000000', creator, textFont = {} } = {}) {
    const W = drawUtils.CANVAS_ACTUAL_WIDTH;
    const H = drawUtils.CANVAS_ACTUAL_HEIGHT;
    const connectorTypes = Object.values(CONNECTOR_TYPES);
    const strokeStyles = Object.values(STROKE_STYLES);
    const elements = [];
    const components = snapTidyComponents(rawComponents);

    components.slice(0, TIDY_LIMITS.MAX).forEach((component) => {
        if (!component || typeof component !== 'object') return;
        const color = HEX_COLOR_RE.test(component.color || '') ? component.color : fallbackColor;

        if (component.type === 'connector') {
            const x1 = clamp(toNumber(component.x1, 0), 0, W);
            const y1 = clamp(toNumber(component.y1, 0), 0, H);
            const x2 = clamp(toNumber(component.x2, 0), 0, W);
            const y2 = clamp(toNumber(component.y2, 0), 0, H);
            if (Math.hypot(x2 - x1, y2 - y1) < TIDY_LIMITS.MIN_CONNECTOR) return;
            const connectorType = connectorTypes.includes(component.connectorType)
                ? component.connectorType
                : CONNECTOR_TYPES.LINE;
            const strokeStyle = strokeStyles.includes(component.strokeStyle)
                ? component.strokeStyle
                : STROKE_STYLES.SOLID;
            elements.push(
                lessonList.buildConnectorElement(connectorType, strokeStyle, x1, y1, x2, y2, 1, color, creator),
            );
            return;
        }

        if (typeof component.content !== 'string' || component.content.trim().length === 0) return;
        const box = clampBox(component, W, H);

        if (component.type === 'text') {
            // The box is where the handwriting was, so the type size follows the box: one line's
            // height, less some leading, as a percentage of the canvas height (the text element's
            // own unit). Keeps the typed text inside the box instead of guessing a size.
            const lines = Math.max(1, component.content.split(/\r?\n/).length);
            const linePx = box.height / lines;
            const fontPct = clamp(((linePx * 0.72) / H) * 100, 1.6, 6);
            const opts = { fontSize: Math.round(fontPct * 10) / 10 };
            if (textFont.fontFamily) opts.fontFamily = textFont.fontFamily;
            if (textFont.fontStyle) opts.fontStyle = textFont.fontStyle;
            const weight = component.fontWeight === 'bold' ? 'bold' : textFont.fontWeight || component.fontWeight;
            if (weight) opts.fontWeight = weight;
            elements.push(
                lessonList.buildTextElement(
                    component.content,
                    box.x,
                    box.y,
                    box.width,
                    box.height,
                    1,
                    color,
                    creator,
                    Object.keys(opts).length > 0 ? opts : undefined,
                ),
            );
        } else if (component.type === 'latex') {
            const el = lessonList.buildLatexElement(
                component.content,
                box.x,
                box.y,
                box.width,
                box.height,
                1,
                color,
                creator,
            );
            // A preview failure must not lose the formula: the element renders from its own
            // latex field later anyway.
            try {
                if (window.latexToImg) addLatexToWindowCache(el.id, window.latexToImg(component.content));
            } catch (error) {
                console.error('Latex preview failed for tidy element', error);
            }
            elements.push(el);
        } else if (component.type === 'svg') {
            elements.push(
                lessonList.buildEmbeddedSvgElement(
                    component.content,
                    box.x,
                    box.y,
                    box.width,
                    box.height,
                    1,
                    color,
                    creator,
                ),
            );
        }
    });

    return elements;
}

const currentPageId = () => boardManager.lessonState && boardManager.lessonState.mainBoardId;

function clearInk() {
    const canvas = drawUtils.getCanvas();
    if (!canvas) throw new Error('NO_CANVAS');
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, drawUtils.CANVAS_ACTUAL_WIDTH, drawUtils.CANVAS_ACTUAL_HEIGHT);
}

// Awaitable twin of LessonPage.loadFullImage without its own save/broadcast.
async function restoreInk(dataUrl) {
    const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('SNAPSHOT_LOAD_FAILED'));
        image.src = dataUrl;
    });
    const canvas = drawUtils.getCanvas();
    if (!canvas) throw new Error('NO_CANVAS');
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, drawUtils.CANVAS_ACTUAL_WIDTH, drawUtils.CANVAS_ACTUAL_HEIGHT);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, drawUtils.CANVAS_ACTUAL_WIDTH, drawUtils.CANVAS_ACTUAL_HEIGHT);
}

// Uploads the current PNG and makes every student reload the page (ink + element list).
async function persistAndBroadcast() {
    boardManager.setDirtyFlag();
    const saved = await cloudBoardManager.saveCurrentMainPage();
    socketUtils.changeMainBoardState(boardManager.lessonState.mainBoardId, boardManager.lessonState.mainBoardPages, true);
    return saved;
}

// Elements in, ink out. The snapshot goes into the local undo cache first, so the handwriting
// is recoverable even past the 30-second revert window. Resolves the session revertTidy needs.
export async function applyTidy({ elements, snapshotDataUrl }) {
    const pageId = currentPageId();
    if (!pageId) throw new Error('NO_PAGE');
    lessonList.saveStageForUndo(pageId, snapshotDataUrl);

    await boardManager.addBatchStickyElements(elements);
    try {
        clearInk();
        await persistAndBroadcast();
    } catch (error) {
        // Elements are on the board but the ink could not be cleared or saved: take them
        // back so the board is exactly as before.
        await boardManager.removeBatchStickyElements(elements.map((el) => el.id));
        throw error;
    }

    return {
        pageId,
        elementIds: elements.map((el) => el.id),
        snapshotDataUrl,
        createdAt: Date.now(),
    };
}

// Removes the received elements and draws the handwriting back, then one reload for the
// students. Refuses when the teacher moved to another page: reverting there would corrupt it.
export async function revertTidy(session) {
    if (!session) return { ok: false, reason: 'NO_SESSION' };
    if (currentPageId() !== session.pageId) return { ok: false, reason: 'PAGE_CHANGED' };
    if (boardManager.mainBoard.isBusy) return { ok: false, reason: 'BUSY' };

    await boardManager.removeBatchStickyElements(session.elementIds);
    await restoreInk(session.snapshotDataUrl);
    await persistAndBroadcast();
    return { ok: true };
}
